import { useMutation } from "@apollo/client";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { SendScoutMessageDocument } from "generated-graphql/graphql";
import { useCallback, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { SCOUT_WRITE_MODE_GQL } from "../hooks/scoutWriteMode";
import { useScoutSkillsLibraryEnabled } from "../useScoutSkillsLibraryEnabled";

const CREATE_SKILL_FROM_CONVERSATION_LABEL = "Create skill from this conversation";

/** Plain markdown, so the new chat reads as if staff typed it and the link opens in-app. */
function buildCreateSkillFromConversationMessage(conversationId: string) {
  return `/create-skill Create a skill based on [this conversation](/staff/observe/scout-conversation/${conversationId}).`;
}

type CreateSkillFromConversationButtonProps = {
  /** Renders nothing without a tenant, since Scout chats are tenant-scoped. */
  tenantId: string | null | undefined;
  conversationId: string;
};

/**
 * Staff-only: starts a new Scout chat owned by the current staff user that
 * links the source conversation, then navigates to it. Callers gate on staff.
 */
export function CreateSkillFromConversationButton({
  tenantId,
  conversationId,
}: CreateSkillFromConversationButtonProps) {
  const navigate = useNavigate();
  const alerts = useAlerts();
  const skillsLibraryEnabled = useScoutSkillsLibraryEnabled();
  const [sendMessage, { loading }] = useMutation(SendScoutMessageDocument, {
    onError: () => alerts.error("Could not start a skill draft from this conversation."),
  });

  const handleClick = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation();
      if (!tenantId) {
        return;
      }
      const result = await sendMessage({
        variables: {
          input: {
            tenantId,
            requestId: crypto.randomUUID(),
            content: buildCreateSkillFromConversationMessage(conversationId),
            writeMode: SCOUT_WRITE_MODE_GQL.ASK,
          },
        },
      });
      const newConversationId = result.data?.sendMessage.conversationId;
      if (newConversationId) {
        navigate(`/o/${tenantId}/scout/${newConversationId}`);
      }
    },
    [conversationId, navigate, sendMessage, tenantId]
  );

  if (!tenantId || !skillsLibraryEnabled) {
    return null;
  }

  return (
    <Tooltip title={CREATE_SKILL_FROM_CONVERSATION_LABEL} placement="left">
      <span>
        <IconButton
          size="small"
          color="staff"
          aria-label={CREATE_SKILL_FROM_CONVERSATION_LABEL}
          disabled={loading}
          onClick={handleClick}
        >
          {loading ? <CircularProgress size={16} /> : <AutoAwesome fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}
